/**
 * PVP Duel Draft Type Configuration
 *
 * Defines the structure and flow of different duel formats.
 * Each draft type specifies the sequence of pick/ban steps.
 */

export interface DuelDraftType {
  /** Unique identifier for this draft type */
  id: string;

  /** Display name */
  name: string;

  /** Brief description */
  description: string;

  /** Total number of pokemon each player must pick */
  total_picks_per_player: number;

  /** Total number of pokemon each player must ban */
  total_bans_per_player: number;

  /** Ordered array of draft steps */
  steps: DraftStep[];
}

export interface DraftStep {
  /** Type of action in this step */
  step_type: "pick" | "ban";

  /** Duration in seconds for players to complete this step */
  duration_seconds: number;

  /** Number of pokemon to pick/ban in this step */
  picks_count: number;
}

/**
 * Predefined duel draft type configurations
 */
export const DUEL_DRAFT_TYPES: Record<string, DuelDraftType> = {
  /**
   * 1 Pick format - Simplest format, single battle
   */
  "1PICK": {
    id: "1PICK",
    name: "1 Pick",
    description: "Pick 1 pokemon for a single battle",
    total_picks_per_player: 1,
    total_bans_per_player: 0,
    steps: [{ step_type: "pick", duration_seconds: 10, picks_count: 1 }],
  },

  /**
   * 3 Pick format - Best of 3 battles
   */
  "3PICK": {
    id: "3PICK",
    name: "3 Pick",
    description: "Pick 3 pokemon for 3 battles",
    total_picks_per_player: 3,
    total_bans_per_player: 0,
    steps: [
      { step_type: "pick", duration_seconds: 10, picks_count: 1 },
      { step_type: "pick", duration_seconds: 10, picks_count: 1 },
      { step_type: "pick", duration_seconds: 10, picks_count: 1 },
    ],
  },

  /**
   * 3 Pick 2 Ban format - Strategic with bans between picks
   */
  "3PICK2BAN": {
    id: "3PICK2BAN",
    name: "3 Pick 2 Ban",
    description: "3 picks with 2 bans in between for strategic play",
    total_picks_per_player: 3,
    total_bans_per_player: 2,
    steps: [
      { step_type: "pick", duration_seconds: 17, picks_count: 1 },
      { step_type: "ban", duration_seconds: 17, picks_count: 1 },
      { step_type: "pick", duration_seconds: 17, picks_count: 1 },
      { step_type: "ban", duration_seconds: 17, picks_count: 1 },
      { step_type: "pick", duration_seconds: 17, picks_count: 1 },
    ],
  },
};

/**
 * Helper to validate if a draft type ID exists
 */
export function isValidDraftType(draftTypeId: string): boolean {
  return draftTypeId in DUEL_DRAFT_TYPES;
}

/**
 * Helper to get a draft type by ID
 */
export function getDraftType(draftTypeId: string): DuelDraftType | undefined {
  return DUEL_DRAFT_TYPES[draftTypeId];
}

/**
 * Helper to get all available draft type IDs
 */
export function getAvailableDraftTypes(): string[] {
  return Object.keys(DUEL_DRAFT_TYPES);
}
